import React, { useCallback, useEffect, useRef, useState } from 'react';
import Form from 'devextreme-react/form';
import 'devextreme-react/autocomplete';
import 'devextreme-react/date-range-box';
import { userService } from '@/core/services/user-service';
import Validator from 'devextreme/ui/validator';
import { roleService } from '@/core/services/role-service';
import { accountService } from '@/core/services/account-service';
import { IUser } from '@/core/interfaces/usestate-interfaces';
import { useNavigate, useParams } from 'react-router-dom';
import ArrayStore from 'devextreme/data/array_store';
import { languageService } from '@/core/services/language-service';
import { ELEMENT_ID } from '@/core/constants/common/element-constants';
import FormWrapper from '@/core/wrappers/form-wrapper';
import { BUTTON_CONSTANTS } from '@/core/constants/common/button-contants';
import { cultureData } from '@/core/datas/culture-data';
import { t } from 'i18next';

function UserDefinition() {
    const formRef: any = useRef<Form>();
    const [accounts, setAccount] = useState([]);
    const [user, setUser] = useState<IUser>({ Id: '', accountId: '', email: '', userName: '', password: '', firstName: '', lastName: '', isEnabled: false, roles: [], languageId: null, culture: '' });
    const [roles, setRoles] = useState([]);
    const [languages, setLanguages] = useState([]);
    const [password, setPassword] = useState('');
    const [cultures, setCultures] = useState({});
    const navigate = useNavigate();
    const params = useParams();
    const userId = params.id;

    const handleSubmit = useCallback(async (): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            try {
                const userForm = formRef.current.props.formData;
                if (userId) {
                    if (userForm.password == undefined || userForm.password == '') delete userForm.password;
                    await userService.updateUser(null, userForm)
                } else {
                    const res: any = await userService.createUser(userForm)
                    navigate(`/user-management/${res.id}`, { state: { id: res.id } })

                }
                resolve();
                navigate(`/admin/user-list`)
            } catch (error) {
                reject(error)
            }

        })
    }, []);

    useEffect(() => {
        (async function () {

            if (userId && user.Id == '') {
                const getUser = await userService.getUser(userId);
                const data = getUser.data;
                setUser({ Id: userId, accountId: data.accountId, email: data.email, userName: data.userName, password: '', firstName: data.firstName, lastName: data.lastName, isEnabled: data.isEnabled, roles: data.roles, languageId: data.languageId, culture: data.culture });
            }
        })();

        const fetchAccounts = async () => {
            const accounts = await accountService.accountList({});
            setAccount(accounts.data)
        };
        const fetchLanguages = async () => {
            const languages = await languageService.getLanguages({});
            setLanguages(languages.data);
        };
        const fetchCultures = async () => {
            setCultures(cultureData);
        };

        fetchAccounts();
        fetchLanguages();
        fetchCultures();
    }, [user])

    useEffect(() => {
        const fetchPermission = async () => {
            const roles = await roleService.lookupRole({});
            setRoles(roles.data)
        };
        fetchPermission();
    }, []);

    const passwordComparison = useCallback<any>(() => password, [password]);
    const changePasswordMode = useCallback((name: any) => {
        if (formRef != null && formRef.current != null) {
            const editor = formRef.current.instance.getEditor(name);
            if (editor)
                editor.option('mode', editor.option('mode') === 'text' ? 'password' : 'text');
        }
    }, []);

    const getPasswordOptions = useCallback(() => ({
        inputAttr: { [ELEMENT_ID]: 'password', autocomplete: 'one-time-code' },
        mode: 'password',
        valueChangeEvent: 'keyup',
        onValueChanged: (e: any) => {
            setPassword(e.value);
            if (formRef != null && formRef.current != null) {
                const editor = formRef.current.instance.getEditor('confirmPassword');
                if (editor != null && editor.option('value')) {
                    const instance = Validator.getInstance(editor.element()) as Validator;
                    instance.validate();
                }
            }
        },
        buttons: [
            {
                name: 'password',
                location: 'after',
                options: {
                    stylingMode: 'text',
                    icon: 'eyeopen',
                    onClick: () => changePasswordMode('password'),
                },
            },
        ],
    }), [changePasswordMode]);

    const getConfirmOptions = useCallback(() => ({
        inputAttr: { [ELEMENT_ID]: 'passwordConfirm', autocomplete: 'one-time-code' },
        mode: 'password',
        valueChangeEvent: 'keyup',
        buttons: [
            {
                name: 'password',
                location: 'after',
                options: {
                    stylingMode: 'text',
                    icon: 'eyeopen',
                    onClick: () => changePasswordMode('confirmPassword'),
                },
            },
        ],
    }), [changePasswordMode]);

    const accountEditorOptions = {
        inputAttr: { [ELEMENT_ID]: 'account' },
        dataSource: accounts,
        displayExpr: 'fullName',
        valueExpr: 'id',
        searchEnabled: true
    };

    const firstNameEditorOptions = {
        inputAttr: { [ELEMENT_ID]: 'firstName' },
    };

    const lastNameEditorOptions = {
        inputAttr: { [ELEMENT_ID]: 'lastName' },
    };

    const emailEditorOptions = {
        inputAttr: { [ELEMENT_ID]: 'email' },
    };

    const userNameEditorOptions = {
        inputAttr: { [ELEMENT_ID]: 'userName', autocomplete: 'one-time-code' },
        disabled: userId ? true : false
    };

    const noDigitsPattern = /^[^0-9]+$/;

    const roleStore = new ArrayStore({
        data: roles,
        key: 'id',
    });

    const roleEditorOptions = {
        inputAttr: { [ELEMENT_ID]: 'roles' },
        dataSource: roleStore,
        displayExpr: "name",
        valueExpr: "id",
        searchEnabled: true,

    };
    const languageEditorOptions = {
        inputAttr: { [ELEMENT_ID]: 'language' },
        dataSource: languages,
        displayExpr: 'description',
        valueExpr: 'id',
        placeholder: undefined,
    };

    const cultureEditorOptions = {
        inputAttr: { [ELEMENT_ID]: 'culture' },
        dataSource: cultures,
        displayExpr: 'culture',
        valueExpr: 'culture',
        placeholder: undefined,
    };

    const isActiveOptions = {
        elementAttr: {
            [ELEMENT_ID]: "isActive",
        },
        value: false,
    };
    const navigateBack = async () => {
        navigate('/user-list', {})
    }
    return (
        <FormWrapper
            onSubmit={handleSubmit}
            formRef={formRef}
            formData={user}
            backButtonNavigationPath='/user-list'
            buttonGroups={[
                { buttonOptions: BUTTON_CONSTANTS.SaveBtn }
            ]}
            simpleItemGroups={[
                {
                    caption: user.Id == '' ? t('new_user') : t('edit_user'),
                    useColCountByScreen: true,
                    items: [
                        {
                            dataField: "firstName",
                            editorOptions: firstNameEditorOptions,
                            label: 'firstname',
                            isRequired: true,
                            requiredMessage: 'firstname',
                            pattern: noDigitsPattern,
                            patternMessage: 'Do not use digits in the Firstname'
                        },
                        {
                            dataField: "lastName",
                            editorOptions: lastNameEditorOptions,
                            label: 'lastname',
                            isRequired: true,
                            requiredMessage: 'lastname',
                            pattern: noDigitsPattern,
                            patternMessage: 'Do not use digits in the Lastname'
                        },
                        {
                            dataField: "email",
                            editorType: "dxTextBox",
                            editorOptions: emailEditorOptions,
                            label: 'email',
                            isRequired: true,
                            requiredMessage: 'email',
                            email: true,
                            emailMessage: 'Email is invalid',
                        },
                        {
                            dataField: "userName",
                            editorOptions: userNameEditorOptions,
                            label: 'username',
                            isRequired: true,
                            requiredMessage: 'username',
                        },
                        {
                            dataField: "password",
                            editorType: "dxTextBox",
                            editorOptions: getPasswordOptions(),
                            label: 'password',
                            isRequired: !userId ? true : false,
                            requiredMessage: 'password',
                            pattern: "^(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?&\\-_\\.])[A-Za-z\\d@$!%*?&\\-_\\.]{8,}$",
                            patternMessage: "password_validation_message",
                        },
                        {
                            dataField: "confirmPassword",
                            editorType: "dxTextBox",
                            editorOptions: getConfirmOptions(),
                            label: 'confirm_password',
                            isRequired: !userId ? true : false,
                            requiredMessage: 'confirm_password',
                            compare: true,
                            compareMessage: "Password and Confirm Password do not match",
                            comparisonTarget: passwordComparison
                        },
                        {
                            dataField: "accountId",
                            editorType: "dxSelectBox",
                            editorOptions: accountEditorOptions,
                            label: 'account',
                            isRequired: true,
                            requiredMessage: 'account',
                        },
                        {
                            dataField: "roles",
                            editorType: "dxTagBox",
                            editorOptions: roleEditorOptions,
                            label: 'roles',
                        },
                        {
                            dataField: "languageId",
                            editorType: "dxSelectBox",
                            editorOptions: languageEditorOptions,
                            label: 'language',
                            isRequired: true,
                            requiredMessage: 'language',
                        },
                        {
                            dataField: "culture",
                            editorType: "dxSelectBox",
                            editorOptions: cultureEditorOptions,
                            label: 'culture',
                            isRequired: true,
                            requiredMessage: 'culture',
                        },
                        {
                            dataField: "isEnabled",
                            editorType: "dxSwitch",
                            editorOptions: isActiveOptions,
                            label: 'user_is_active',
                        }
                    ]
                }
            ]}
        />
    )
}

export default UserDefinition;
